import type { DecisionAudit } from '@kkp-fraud/shared';
import {
  rulePrecision,
  type Case,
  type CaseFilters,
  type CaseHistoryEntry,
  type CaseLabel,
  type CasePage,
  type CaseStatus,
  type InvestigateDecision,
  type InvestigatePage,
  type RulePrecisionReport,
} from '../cases';
import { DEMO_USERS } from './audit';

/**
 * Investigate for `VITE_DEMO=1`: cases in every status, opened on the demo decision feed's own
 * rows (`./decisions`), so a case's snapshot, the card's timeline and the precision table all
 * agree with Monitor and Report. Read-only, like the demo master lists: every write goes to the
 * real admin service, and the screen offers none on a demo case or a demo decision.
 *
 * Ids end in `-DEMO` + two digits — never a shape the admin service mints.
 */

const DEMO_ID = /-DEMO\d{2}$/;

export function isDemoCaseId(id: string): boolean {
  return DEMO_ID.test(id);
}

/** The server's snapshot of a decision (`CaseSnapshot.From`), from the fields a demo row has. */
export function snapshotOf(d: DecisionAudit): InvestigateDecision {
  const seen = new Set<string>();
  const firedRules = [
    ...d.hits.map((h) => ({ ruleId: h.rule_id, name: h.name, action: h.action, detail: h.detail, ...(h.branch ? { branch: h.branch } : {}) })),
    ...d.reasons.map((r) => ({ ruleId: r })),
  ].filter((r) => r.ruleId !== 'FAIL_SAFE' && !seen.has(r.ruleId) && seen.add(r.ruleId));
  const txn = d.txn;
  return {
    decisionId: d.id,
    cardId: d.cardId,
    ...(d.customerId ? { customerId: d.customerId } : {}),
    ...(txn?.merchant_id ? { merchantId: txn.merchant_id } : {}),
    decisionUtc: d.eventTimeUtc,
    decision: d.decision,
    ruleSetVersion: d.ruleSetVersion,
    engineVersion: d.engineVersion,
    degraded: d.degraded,
    engineLatencyMs: d.engineLatencyMs,
    serviceLatencyMs: d.serviceLatencyMs,
    firedRules,
    skippedRules: [],
    stateIncomplete: d.stateIncomplete ?? [],
    reasons: d.reasons,
    txn: {
      transactionId: d.transactionId,
      cardId: d.cardId,
      ...(d.customerId ? { customerId: d.customerId } : {}),
      ...(txn?.merchant_id ? { merchantId: txn.merchant_id } : {}),
      ...(txn?.merchant_name ? { merchantName: txn.merchant_name } : {}),
      ...(txn?.channel_id ? { channelId: txn.channel_id } : {}),
      ...(txn?.entry_mode ? { entryMode: txn.entry_mode } : {}),
      ...(txn?.currency_code_num != null ? { currencyCodeNum: txn.currency_code_num } : {}),
      amountThb: d.amountThb,
      ...(d.mcc != null ? { mcc: d.mcc } : {}),
      ...(d.merchantCountryNum != null ? { merchantCountryNum: d.merchantCountryNum } : {}),
      network: d.network,
    },
  };
}

/** Where each demo case has got to: the path through the workflow after "opened". */
const PATHS: { path: CaseStatus[]; note?: string; assignee?: string; priority?: Case['priority'] }[] = [
  { path: [] },
  { path: [], priority: 'high' },
  { path: ['UnderReview'], assignee: DEMO_USERS.admin, priority: 'high', note: 'Called the cardholder, no answer yet.' },
  { path: ['UnderReview'], assignee: DEMO_USERS.checker },
  { path: ['UnderReview', 'ConfirmedFraud'], assignee: DEMO_USERS.admin, priority: 'high', note: 'Cardholder did not make this purchase.' },
  { path: ['UnderReview', 'FalsePositive'], assignee: DEMO_USERS.checker, note: 'Regular merchant for this card.' },
  { path: ['UnderReview', 'Genuine'], assignee: DEMO_USERS.admin, note: 'Cardholder confirmed by phone.' },
  { path: ['UnderReview', 'ConfirmedFraud', 'Closed'], assignee: DEMO_USERS.checker, priority: 'medium' },
  { path: ['UnderReview', 'FalsePositive', 'Closed'], assignee: DEMO_USERS.admin, priority: 'low' },
  { path: ['UnderReview', 'Genuine', 'Closed'], assignee: DEMO_USERS.checker },
  // Opened by mistake: closed straight away, without an outcome.
  { path: ['Closed'], assignee: DEMO_USERS.admin, note: 'Opened on the wrong decision.' },
];

/** The workflow's next steps — for the demo cases only; a real case carries the BFF's own list. */
const NEXT: Record<CaseStatus, CaseStatus[]> = {
  Open: ['UnderReview', 'Closed'],
  UnderReview: ['ConfirmedFraud', 'FalsePositive', 'Genuine'],
  ConfirmedFraud: ['Closed', 'UnderReview'],
  FalsePositive: ['Closed', 'UnderReview'],
  Genuine: ['Closed', 'UnderReview'],
  Closed: ['UnderReview'],
};

const pad = (n: number) => String(n).padStart(2, '0');

function idAt(ms: number, i: number): string {
  const d = new Date(ms);
  const stamp = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}${String(d.getUTCMilliseconds()).padStart(3, '0')}`;
  return `CASE-${stamp}-DEMO${pad(i)}`;
}

/**
 * One case per path above, opened on the newest decisions that fired a rule (one per card),
 * each some minutes after its decision and moved on at ten-minute steps. Newest first.
 */
export function demoCases(rows: readonly DecisionAudit[]): Case[] {
  const picked: DecisionAudit[] = [];
  const cards = new Set<string>();
  for (const d of rows) {
    if (picked.length === PATHS.length) break;
    if (d.decision === 'APPROVE' || d.reasons.length === 0 || d.degraded || cards.has(d.cardId)) continue;
    cards.add(d.cardId);
    picked.push(d);
  }
  const out = picked.map((d, i): Case => {
    const plan = PATHS[i]!;
    const snap = snapshotOf(d);
    const opened = Date.parse(d.eventTimeUtc) + 4 * 60_000;
    const creator = i % 2 === 0 ? DEMO_USERS.admin : DEMO_USERS.checker;
    const history: CaseHistoryEntry[] = [{ utc: new Date(opened).toISOString(), actor: creator, to: 'Open' }];
    let status: CaseStatus = 'Open';
    let resolution: CaseLabel | undefined;
    plan.path.forEach((to, k) => {
      const why = status === 'Open' && to === 'Closed' ? plan.note : undefined;
      history.push({ utc: new Date(opened + (k + 1) * 600_000).toISOString(), actor: plan.assignee ?? creator, from: status, to, ...(why ? { note: why } : {}) });
      status = to;
      if (to === 'ConfirmedFraud' || to === 'FalsePositive' || to === 'Genuine') resolution = to;
    });
    const updated = history[history.length - 1]!;
    const { decisionId, ...rest } = snap;
    return {
      ...rest,
      id: idAt(opened, i + 1),
      decisionId,
      status,
      ...(resolution ? { resolution } : {}),
      ...(plan.assignee ? { assignee: plan.assignee } : {}),
      ...(plan.priority ? { priority: plan.priority } : {}),
      notes: plan.note ? [{ id: `N-${pad(i + 1)}`, author: plan.assignee ?? creator, utc: updated.utc, text: plan.note }] : [],
      history,
      createdUtc: history[0]!.utc,
      createdBy: creator,
      updatedUtc: updated.utc,
      updatedBy: updated.actor,
      _etag: `"${i + 1}"`,
      allowedTransitions: NEXT[status],
      noteEditMinutes: 15,
    };
  });
  return out.sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

function matches(c: Case, f: CaseFilters): boolean {
  const eq = (want: string | undefined, have: string | undefined) => !want?.trim() || want.trim() === have;
  return (
    eq(f.status, c.status) &&
    eq(f.resolution, c.resolution) &&
    (!f.ruleId?.trim() || c.firedRules.some((r) => r.ruleId === f.ruleId!.trim())) &&
    eq(f.merchantId, c.merchantId) &&
    eq(f.customerId, c.customerId) &&
    eq(f.cardId, c.cardId) &&
    eq(f.assignee, c.assignee)
  );
}

/** The demo cases matching the filters, in front of the real page. */
export function mergeCasePage(demo: readonly Case[], filters: CaseFilters, real: CasePage, firstPage: boolean): CasePage {
  const mine = firstPage ? demo.filter((c) => matches(c, filters)) : [];
  return { ...real, items: [...mine, ...real.items.filter((c) => !isDemoCaseId(c.id))] };
}

/** The card's demo decisions, newest first, with the demo case of each that has one. No cursor: the feed is one day. */
export function demoTimeline(rows: readonly DecisionAudit[], cases: readonly Case[], cardId: string): InvestigatePage {
  const byDecision = new Map(cases.map((c) => [c.decisionId, c.id]));
  const items = rows
    .filter((d) => d.cardId === cardId)
    .map((d) => {
      const s = snapshotOf(d);
      const caseId = byDecision.get(s.decisionId);
      return caseId ? { ...s, caseId } : s;
    });
  return { cardId, source: 'engine', items };
}

/** Precision over the demo cases, added to the real counts rule by rule. */
export function mergePrecision(demo: readonly Case[], real: RulePrecisionReport): RulePrecisionReport {
  const rows = new Map(rulePrecision(demo).map((r) => [r.ruleId, r]));
  for (const r of real.rules) {
    const d = rows.get(r.ruleId);
    if (!d) {
      rows.set(r.ruleId, r);
      continue;
    }
    const sum = {
      ...d,
      ...(d.name ?? r.name ? { name: d.name ?? r.name } : {}),
      confirmedFraud: d.confirmedFraud + r.confirmedFraud,
      falsePositive: d.falsePositive + r.falsePositive,
      genuine: d.genuine + r.genuine,
      open: d.open + r.open,
    };
    const labelled = sum.confirmedFraud + sum.falsePositive + sum.genuine;
    rows.set(r.ruleId, { ...sum, labelled, precision: labelled === 0 ? null : sum.confirmedFraud / labelled });
  }
  return {
    ...real,
    // A case closed without an outcome is left out of precision and of its count, as the BFF does.
    cases: real.cases + demo.filter((c) => !(c.status === 'Closed' && !c.resolution)).length,
    rules: [...rows.values()].sort((a, b) => (a.ruleId < b.ruleId ? -1 : a.ruleId > b.ruleId ? 1 : 0)),
  };
}
